import {
  createContext,
  type ReactNode,
  type Ref,
  useContext,
  useId,
} from 'react'
import cx from 'classnames'
import type { PressEventHandler } from '@/infra/prop-types'
import { Button as AriaButton } from 'react-aria-components'
import { StyledLink } from '@/components/Link'
import { Icon } from '@/components/Icon'
import styles from './NoticeBanner.module.css'
import { useChromeThemeColor, ChromeThemeLayer } from '@/features/shell/theming'
import { useCssVariable } from '@/features/shell/theming/use-css-variable'
import { useIsSpacious } from '@/hooks/viewport-hooks'

export interface NoticeBannerProps {
  children?: ReactNode

  /**
   * Ref to the banner element.
   */
  ref?: Ref<HTMLDivElement>

  topOffset?: number

  testId?: string
}

/**
 * Used for passing the ID of the notice banner to the close button.
 */
const NoticeBannerIdContext = createContext<string | undefined>(undefined)

/**
 * Shows a notice at the top of the screen.
 */
export function NoticeBanner({
  ref,
  children,
  topOffset,
  testId,
}: NoticeBannerProps) {
  const id = useId()
  const isSpacious = useIsSpacious()

  useChromeThemeColor(
    useCssVariable(
      // Only change the chrome theme to the banner color if the notice banner
      // will span the entire screen width.
      isSpacious ? '--border-color-default' : '--surface-notification-theme',
    ),
    ChromeThemeLayer.Banner,
  )

  return (
    <div
      ref={ref}
      id={id}
      role="banner"
      aria-label="Notice"
      className={cx(styles.root)}
      data-test={testId}
      style={
        topOffset
          ? { top: `calc(var(--app-safe-area-inset-top) + ${topOffset}px)` }
          : undefined
      }
    >
      <NoticeBannerIdContext value={id}>{children}</NoticeBannerIdContext>
    </div>
  )
}

interface NoticeBannerTextProps {
  children?: ReactNode
}

/**
 * Notice Banner Text
 */
export function NoticeBannerText({ children }: NoticeBannerTextProps) {
  return <p className={styles.text}>{children}</p>
}

interface NoticeBannerActionProps {
  children?: ReactNode
  href?: string
  to?: string
  onPress?: PressEventHandler
  target?: HTMLAnchorElement['target']
}

/**
 * Notice Banner Action
 */
export function NoticeBannerAction({
  children,
  ...rest
}: NoticeBannerActionProps) {
  return (
    <span className={styles.action}>
      <StyledLink {...rest} variant="white">
        {children}
      </StyledLink>
    </span>
  )
}

export interface NoticeBannerCloseButtonProps {
  onPress?: PressEventHandler
  testId?: string
}

/**
 * Notice Banner Close Button
 */
export function NoticeBannerCloseButton({
  onPress,
  testId,
}: NoticeBannerCloseButtonProps) {
  const bannerId = useContext(NoticeBannerIdContext)
  return (
    <div className={styles.closeButtonContainer}>
      <AriaButton
        aria-label="Close"
        onPress={onPress}
        aria-controls={bannerId}
        data-test={testId}
        className={({ isPressed, isFocusVisible, isHovered }) =>
          cx(
            styles.closeButton,
            isPressed && styles.pressed,
            isHovered && styles.hover,
            isFocusVisible && styles.focusVisible,
          )
        }
      >
        <Icon name="close" />
      </AriaButton>
    </div>
  )
}
